import { type RouteObject } from 'react-router-dom';
import { lazy } from 'react';
import AdminLayout from '../components/AdminLayout';

// Main pages from project-5093638
const HomePage = lazy(() => import('../pages/home/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const SolutionPage = lazy(() => import('../pages/solution/page'));
const TechnologyPage = lazy(() => import('../pages/technology/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ResourcesPage = lazy(() => import('../pages/resources/page'));
const SupportPage = lazy(() => import('../pages/support/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
const SolutionOverviewPage = lazy(
  () => import('../pages/solution/overview/page'),
);
const VoicePhishingPage = lazy(
  () => import('../pages/solution/voice-phishing/page'),
);
const DigitalCarePage = lazy(
  () => import('../pages/solution/digital-care/page'),
);
const ComparisonPage = lazy(() => import('../pages/solution/comparison/page'));
const DASPage = lazy(() => import('../pages/technology/das/page'));
const GatekeeperPage = lazy(
  () => import('../pages/technology/gatekeeper/page'),
);
const AssistantPage = lazy(() => import('../pages/technology/assistant/page'));
const DemoPage = lazy(() => import('../pages/support/demo/page'));
const SolutionsForPage = lazy(() => import('../pages/solutions-for/page'));
const UserGuidePage = lazy(() => import('../pages/resources/user-guide/page'));
const FAQPage = lazy(() => import('../pages/resources/faq/page'));
const DemoVideosPage = lazy(
  () => import('../pages/resources/demo-videos/page'),
);
const DownloadsPage = lazy(() => import('../pages/resources/downloads/page'));
const AdminLogin = lazy(() => import('../pages/admin/login/page'));
const AdminNewsroom = lazy(() => import('../pages/admin/newsroom/page'));
const AdminWhitepapers = lazy(() => import('../pages/admin/whitepapers/page'));
const AdminFAQs = lazy(() => import('../pages/admin/faqs/page'));
const AdminBlogs = lazy(() => import('../pages/admin/blogs/page'));
const NewsPage = lazy(() => import('../pages/resources/news/page'));
const BlogPage = lazy(() => import('../pages/resources/blog/page'));

// Admin system from extsystem
const Chatbot = lazy(() => import('../pages/chatbot/page'));
const ChatbotAdmin = lazy(() => import('../pages/admin/chatbot/page'));
const RecruitmentAdmin = lazy(() => import('../pages/recruitment/admin'));
const AdminDashboard = lazy(() => import('../pages/admin/dashboard'));
const TrialPage = lazy(() => import('../pages/trial/page'));
const TrialAdmin = lazy(() => import('../pages/trial/admin'));

// Content boards
const NewsPageList = lazy(() => import('../pages/news/page'));
const NewsDetail = lazy(() => import('../pages/news/detail'));
const NewsAdmin = lazy(() => import('../pages/news/admin'));
const BlogPageList = lazy(() => import('../pages/blog/page'));
const BlogDetail = lazy(() => import('../pages/blog/detail'));
const BlogAdmin = lazy(() => import('../pages/blog/admin'));
const WhitepaperPageList = lazy(() => import('../pages/whitepaper/page'));
const WhitepaperDetail = lazy(() => import('../pages/whitepaper/detail'));
const WhitepaperAdmin = lazy(() => import('../pages/whitepaper/admin'));
const DemoVideosAdmin = lazy(() => import('../pages/demo-videos/admin'));

const ResourcesWhitepaperPage = lazy(
  () => import('../pages/resources/whitepaper/page'),
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/solution',
    element: <SolutionPage />,
  },
  {
    path: '/solution/overview',
    element: <SolutionOverviewPage />,
  },
  {
    path: '/solution/voice-phishing',
    element: <VoicePhishingPage />,
  },
  {
    path: '/solution/digital-care',
    element: <DigitalCarePage />,
  },
  {
    path: '/solution/comparison',
    element: <ComparisonPage />,
  },
  {
    path: '/solutions-for',
    element: <SolutionsForPage />,
  },
  {
    path: '/technology',
    element: <TechnologyPage />,
  },
  {
    path: '/technology/das',
    element: <DASPage />,
  },
  {
    path: '/technology/gatekeeper',
    element: <GatekeeperPage />,
  },
  {
    path: '/technology/assistant',
    element: <AssistantPage />,
  },
  {
    path: '/about',
    element: <AboutPage />,
  },
  {
    path: '/resources',
    element: <ResourcesPage />,
  },
  {
    path: '/resources/user-guide',
    element: <UserGuidePage />,
  },
  {
    path: '/resources/faq',
    element: <FAQPage />,
  },
  {
    path: '/resources/demo-videos',
    element: <DemoVideosPage />,
  },
  {
    path: '/resources/downloads',
    element: <DownloadsPage />,
  },
  {
    path: '/resources/whitepaper',
    element: <ResourcesWhitepaperPage />,
  },
  {
    path: '/resources/news',
    element: <NewsPage />,
  },
  {
    path: '/resources/blog',
    element: <BlogPage />,
  },
  {
    path: '/support',
    element: <SupportPage />,
  },
  {
    path: '/support/demo',
    element: <DemoPage />,
  },
  {
    path: '/admin/login',
    element: <AdminLogin />,
  },
  {
    path: '/admin',
    element: <AdminDashboard />,
  },
  {
    path: '/admin/chatbot',
    element: (
      <AdminLayout>
        <ChatbotAdmin />
      </AdminLayout>
    ),
  },
  {
    path: '/admin/recruitment',
    element: (
      <AdminLayout>
        <RecruitmentAdmin />
      </AdminLayout>
    ),
  },
  {
    path: '/admin/trial',
    element: (
      <AdminLayout>
        <TrialAdmin />
      </AdminLayout>
    ),
  },
  {
    path: '/admin/newsroom',
    element: <AdminNewsroom />,
  },
  {
    path: '/admin/whitepapers',
    element: <AdminWhitepapers />,
  },
  {
    path: '/admin/faqs',
    element: <AdminFAQs />,
  },
  {
    path: '/admin/blogs',
    element: <AdminBlogs />,
  },
  {
    path: '/chatbot',
    element: <Chatbot />,
  },
  {
    path: '/trial',
    element: <TrialPage />,
  },
  {
    path: '/news',
    element: <NewsPageList />,
  },
  {
    path: '/news/:slug',
    element: <NewsDetail />,
  },
  {
    path: '/admin/news',
    element: <NewsAdmin />,
  },
  {
    path: '/blog',
    element: <BlogPageList />,
  },
  {
    path: '/blog/:slug',
    element: <BlogDetail />,
  },
  {
    path: '/admin/blog',
    element: <BlogAdmin />,
  },
  {
    path: '/whitepaper',
    element: <WhitepaperPageList />,
  },
  {
    path: '/whitepaper/:slug',
    element: <WhitepaperDetail />,
  },
  {
    path: '/admin/whitepaper',
    element: <WhitepaperAdmin />,
  },
  {
    path: '/admin/demo-videos',
    element: <DemoVideosAdmin />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
